import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { theme } from "../theme.js";

export default function RaceProfile({ trackData, title = "Profil d'élévation" }) {
  // Define calculateDistance before useMemo so it can be used inside
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; // Earth's radius in meters
    const phi1 = (Math.PI * lat1) / 180;
    const phi2 = (Math.PI * lat2) / 180;
    const deltaPhi = (Math.PI * (lat2 - lat1)) / 180;
    const deltaLambda = (Math.PI * (lon2 - lon1)) / 180;

    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) * Math.cos(phi2) * Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const profileData = useMemo(() => {
    if (!trackData) return [];

    try {
      console.log("[DEBUG RaceProfile] trackData type:", typeof trackData);
      
      // Check if trackData is an object with properties (the full response) or direct geojson
      if (trackData.geojson_simplified) {
        console.log("[DEBUG RaceProfile] Found geojson_simplified in trackData");
        trackData = trackData.geojson_simplified;
      }

      // Parse track data if needed
      let geoJsonData = trackData;
      if (typeof trackData === "string") {
        geoJsonData = JSON.parse(trackData);
      }

      // Extract geometry
      let geometry = geoJsonData.geometry || geoJsonData;

      if (!geometry || !geometry.coordinates || geometry.coordinates.length === 0) {
        console.log("[DEBUG RaceProfile] No coordinates found");
        return [];
      }

      const coords = geometry.coordinates;
      console.log("[DEBUG RaceProfile] First coordinate:", coords[0], "Length of coord array:", coords[0]?.length);
      console.log("[DEBUG RaceProfile] Total coordinates:", coords.length);

      const profilePoints = [];
      let cumulativeDistance = 0;
      let hasElevation = false;

      for (let i = 0; i < coords.length; i++) {
        const coord = coords[i];
        const lon = coord[0];
        const lat = coord[1];
        const ele = coord[2];

        if (i === 0 || i === 1 || i === coords.length - 1) {
          console.log(`[DEBUG RaceProfile] Coord ${i}:`, coord, "-> ele:", ele, "type:", typeof ele);
        }

        if (ele !== undefined && ele !== null && ele !== 0) {
          hasElevation = true;
        }

        // Calculate distance from previous point
        if (i > 0) {
          const prevCoord = coords[i - 1];
          const prevLon = prevCoord[0];
          const prevLat = prevCoord[1];
          const dist = calculateDistance(prevLat, prevLon, lat, lon);
          cumulativeDistance += dist;
        }

        profilePoints.push({
          distance: cumulativeDistance / 1000, // Convert to km
          elevation: ele !== undefined ? ele : 0,
          index: i,
        });
      }

      console.log("[DEBUG RaceProfile] Has elevation data:", hasElevation);
      console.log("[DEBUG RaceProfile] First 3 profile elevations:", profilePoints.slice(0, 3).map(p => p.elevation));
      console.log("[DEBUG RaceProfile] Profile points count:", profilePoints.length);

      return profilePoints;
    } catch (error) {
      console.error("[DEBUG RaceProfile] Error:", error);
      return [];
    }
  }, [trackData]);

  if (!profileData || profileData.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.errorMessage}>
          Aucune donnée d'élévation disponible. Vérifiez que le fichier GPX contient les informations d'altitude.
        </div>
      </div>
    );
  }

  // Calculate min and max for scaling
  const elevations = profileData.map((p) => p.elevation);
  const minEle = Math.min(...elevations);
  const maxEle = Math.max(...elevations);
  
  // Check if all elevations are 0 (no elevation data)
  const hasElevationData = elevations.some((e) => e !== 0);
  
  if (!hasElevationData) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.title}>{title}</h3>
        </div>
        <div style={styles.errorMessage}>
          Le fichier GPX ne contient pas de données d'altitude (elevation). 
          Assurez-vous que le fichier GPX inclut les informations d'élévation.
        </div>
      </div>
    );
  }

  const maxDistance = profileData[profileData.length - 1].distance;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipDist}>{Number(payload[0].payload.distance).toFixed(2)} km</p>
          <p style={styles.tooltipEle}>{Math.round(payload[0].value)} m</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>{title}</h3>
        <div style={styles.stats}>
          <span style={styles.statItem}>
            <strong>Distance:</strong> {maxDistance.toFixed(2)} km
          </span>
          <span style={styles.statItem}>
            <strong>Élévation min:</strong> {Math.round(minEle)} m
          </span>
          <span style={styles.statItem}>
            <strong>Élévation max:</strong> {Math.round(maxEle)} m
          </span>
        </div>
      </div>
      <div style={styles.chartContainer}>
        <div style={{ minWidth: 420, width: "100%" }}>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={profileData} margin={{ top: 10, right: 20, left: 10, bottom: 30 }}>
            <defs>
              <linearGradient id="elevGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.colors.primary} stopOpacity={0.25} />
                <stop offset="95%" stopColor={theme.colors.primary} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="4 4" stroke={theme.colors.border} vertical={false} />
            <XAxis
              dataKey="distance"
              type="number"
              domain={[0, maxDistance]}
              tickFormatter={(v) => `${v.toFixed(1)} km`}
              tick={{ fontSize: 12, fill: theme.colors.textInverted }}
              label={{ value: "Distance (km)", position: "insideBottom", offset: -15, fontSize: 13, fill: theme.colors.textInverted }}
            />
            <YAxis
              domain={[minEle - 10, maxEle + 10]}
              tickFormatter={(v) => `${Math.round(v)} m`}
              tick={{ fontSize: 12, fill: theme.colors.textInverted }}
              width={55}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="elevation"
              stroke={theme.colors.primary}
              strokeWidth={2}
              fill="url(#elevGradient)"
              dot={false}
              activeDot={{ r: 4, fill: theme.colors.primary }}
            />
          </AreaChart>
        </ResponsiveContainer>        </div>      </div>
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    borderRadius: theme.radius.lg,
    overflow: "hidden",
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.white,
    marginBottom: theme.spacing.lg,
    position: "relative",
    zIndex: 0,
  },
  header: {
    padding: theme.spacing.md,
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: "#f9fafb",
  },
  title: {
    fontSize: theme.fonts.size.lg,
    fontWeight: theme.fonts.weight.semibold,
    color: theme.colors.textInverted,
    margin: `0 0 ${theme.spacing.sm} 0`,
  },
  stats: {
    display: "flex",
    gap: theme.spacing.lg,
    fontSize: theme.fonts.size.sm,
    color: theme.colors.textMuted,
    flexWrap: "wrap",
  },
  statItem: {
    display: "flex",
    gap: theme.spacing.xs,
  },
  chartContainer: {
    padding: `${theme.spacing.lg} 0`,
    width: "100%",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    boxSizing: "border-box",
  },
  tooltip: {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radius.md,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    boxShadow: theme.shadows.sm,
  },
  tooltipDist: {
    margin: 0,
    fontSize: 12,
    color: theme.colors.textMuted,
  },
  tooltipEle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: theme.colors.primary,
  },
  errorMessage: {
    padding: theme.spacing.lg,
    textAlign: "center",
    color: theme.colors.error,
    fontSize: theme.fonts.size.sm,
  },
};
